import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { twJoin } from 'tailwind-merge'
import { Button, Switch } from 'components/buttons'
import { Checkbox } from 'components/form/checkbox'
import { Spinner } from 'components/loaders'
import { useFacilities } from '@hooks/useFacilities'
import {
  useUpdateUserAdminRoles,
  useUserAdminRoles,
} from '@hooks/useUserAdminRoles'
import { toast } from '@lib/toasts'

interface AdminRolesFormProps {
  user: User
  // Super admins may not revoke their own super admin role, which would
  // immediately lock them out of granting it back.
  isSignedInUser: boolean
  onDone: () => void
}

const RoleHeading: FC<{ title: string; description: string }> = ({
  title,
  description,
}) => (
  <div className="flex flex-col gap-1">
    <span className="text-sm font-medium text-neutral-900">{title}</span>
    <span className="text-xs text-neutral-600">{description}</span>
  </div>
)

const sameFacilityIds = (a: number[], b: number[]) => {
  if (a.length !== b.length) return false

  const ids = new Set(b)
  return a.every((id) => ids.has(id))
}

export const AdminRolesForm: FC<AdminRolesFormProps> = ({
  user,
  isSignedInUser,
  onDone,
}) => {
  const { t } = useTranslation()
  const { data: facilities } = useFacilities()
  const { data: adminRoles } = useUserAdminRoles(user.id)
  const { mutate: updateAdminRoles, isLoading: saving } =
    useUpdateUserAdminRoles(user.id)

  // Edits are held here until Save, so that a stray click on the switch or a
  // checkbox does not grant anybody anything.
  const [draft, setDraft] = useState<Partial<UserAdminRoles>>({})

  const values = useMemo(
    () => (adminRoles ? { ...adminRoles, ...draft } : undefined),
    [adminRoles, draft],
  )

  // Only the fields that actually differ from what is saved, which is both what
  // gets PATCHed and what decides whether there is anything to save.
  const changes = useMemo<Partial<UserAdminRoles>>(() => {
    if (!adminRoles || !values) return {}

    return {
      ...(values.superAdmin !== adminRoles.superAdmin && {
        superAdmin: values.superAdmin,
      }),
      ...(!sameFacilityIds(
        values.timeLogAdminFacilityIds,
        adminRoles.timeLogAdminFacilityIds,
      ) && { timeLogAdminFacilityIds: values.timeLogAdminFacilityIds }),
    }
  }, [adminRoles, values])

  const dirty = Object.keys(changes).length > 0

  const save = () =>
    updateAdminRoles(changes, {
      onSuccess: () => {
        onDone()
        toast({
          title: t('features.usersAdmin.userDetails.adminRoles.rolesUpdated'),
          variant: 'success',
          content: t(
            'features.usersAdmin.userDetails.adminRoles.successfullyUpdatedRoles',
            { name: user.name },
          ),
        })
      },
      onError: () =>
        toast({
          title: t('common.error'),
          variant: 'error',
          content: t(
            'features.usersAdmin.userDetails.adminRoles.failedToUpdateRoles',
          ),
        }),
    })

  const toggleTimeLogAdminFacility = (facilityId: number, granted: boolean) => {
    if (!values) return

    const facilityIds = granted
      ? [...values.timeLogAdminFacilityIds, facilityId]
      : values.timeLogAdminFacilityIds.filter((id) => id !== facilityId)

    setDraft((current) => ({
      ...current,
      timeLogAdminFacilityIds: facilityIds,
    }))
  }

  return (
    <div>
      <div className="flex flex-col gap-6 px-6 pb-6 w-[560px]">
        {!values || !facilities ? (
          <Spinner />
        ) : (
          <>
            <div className="flex flex-row items-start gap-3">
              <Switch
                checked={values.superAdmin}
                disabled={saving || isSignedInUser}
                onChange={(superAdmin) =>
                  setDraft((current) => ({ ...current, superAdmin }))
                }
              />
              <div className="flex flex-col gap-1">
                <RoleHeading
                  title={t(
                    'features.usersAdmin.userDetails.adminRoles.superAdmin',
                  )}
                  description={t(
                    'features.usersAdmin.userDetails.adminRoles.superAdminDescription',
                  )}
                />
                {isSignedInUser && (
                  <span className="text-xs italic text-neutral-500">
                    {t(
                      'features.usersAdmin.userDetails.adminRoles.cannotChangeYourOwnSuperAdminRole',
                    )}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <RoleHeading
                title={t(
                  'features.usersAdmin.userDetails.adminRoles.timeLogAdmin',
                )}
                description={t(
                  'features.usersAdmin.userDetails.adminRoles.timeLogAdminDescription',
                )}
              />
              {values.superAdmin ? (
                <span className="text-sm text-neutral-600">
                  {t(
                    'features.usersAdmin.userDetails.adminRoles.superAdminsAdministerEveryFacility',
                  )}
                </span>
              ) : (
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {facilities.map((facility) => {
                    const granted = values.timeLogAdminFacilityIds.includes(
                      facility.id,
                    )

                    return (
                      <label
                        key={facility.id}
                        className={twJoin(
                          'flex items-center gap-2 text-sm text-neutral-900',
                          saving ? 'cursor-not-allowed' : 'cursor-pointer',
                        )}
                      >
                        <Checkbox
                          checked={granted}
                          disabled={saving}
                          onChange={() =>
                            toggleTimeLogAdminFacility(facility.id, !granted)
                          }
                        />
                        {facility.name}
                      </label>
                    )
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <div className="flex justify-end w-full px-6 py-4 border-t border-neutral-300">
        <Button
          variant="subtle"
          className="font-normal"
          disabled={saving}
          onClick={onDone}
        >
          {t('common.cancel')}
        </Button>

        <Button disabled={!dirty || saving} loading={saving} onClick={save}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  )
}
